import assert from 'assert';
import type { ResultOf } from '@graphql-typed-document-node/core';
import type { LDProps } from 'launchdarkly-react-client-sdk';
import type { ItemType } from 'src/constants/account-link';
import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate, Deprecated_ItemsUpdateObject } from 'src/types/account';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { TableRow } from '@savvy-kit/components/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Text } from '@savvy-kit/foundations/text';
import { useMediaQuery } from '@savvy-kit/hooks/use-media-query';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { ChevronRight, Info, Maximize } from 'lucide-react';
import { DateTime } from 'luxon';
import ColorCodedPerformanceTypography from 'src/components/performance-report/views/color-coded-return';
import { graphql, unmaskFragment } from 'src/gql';
import {
  accountValueTitle,
  getHeldawayAccountURI,
  getManagedAccountURI,
  isDisconnectedLinkedAccount,
  padBulletsToMaskedAccountNumber,
  providerSourceDisplayName,
} from 'src/utils/account';
import { assetValueSubtitle, assetValueTitle } from 'src/utils/asset';
import { liabilityValueTitle } from 'src/utils/liability';
import { performanceFromQuickDate } from 'src/utils/perf-reporting';
import { portfolioItemSecondaryValueSubtitle } from 'src/utils/portfolio-item-display';
import { useInterval } from 'usehooks-ts';

import type { ProviderAccountSource } from '@savvy/constants/generated/gql-enums';
import type {
  AccountCustodyName,
  PerformanceTimePeriodName,
  PortfolioItemTypeName,
} from '@savvy/constants/generated/gql-types';
import type { ColumnDef, Row } from '@savvy/data-table';
import type { ID } from '@savvy/utils/id';
import { DataTable } from '@savvy/data-table';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { textColors } from '@savvy/deprecated-shared/theme';

import AddItemDialog from '../add-item/views/add-item-dialog';
import { ViewAccountDialog } from '../portfolio/account/view-account-dialog';
import ViewAssetDialog from '../portfolio/asset/view-asset-dialog';
import ViewLiabilityDialog from '../portfolio/liability/view-liability-dialog';
import NetWorthAccountDescriptionRow from './net-worth-account-description-row';
import { NetWorthAccountGroupTableTitle } from './net-worth-account-group-table-title';
import { NetWorthAccountMenu } from './net-worth-account-menu';
import { NetWorthAccountProductTypeTag } from './net-worth-account-product-type-tag';
import { NetWorthAccountSleeveRow } from './net-worth-account-sleeve-row';
import NetWorthAssetDescriptionRow from './net-worth-asset-description-row';
import { NetWorthAssetMenu } from './net-worth-asset-menu';
import NetWorthLiabilityDescriptionRow from './net-worth-liability-description-row';
import { NetWorthLiabilityMenu } from './net-worth-liability-menu';

export const NetWorthAccountGroupTableFragment = graphql(/* GraphQL */ `
  fragment NetWorthAccountGroupTable on PortfolioItemGroup {
    ...NetWorthAccountGroupTableTitle
    name
    accounts {
      ...NetWorthAccountDescriptionRow
      ...NetWorthAccountMenu
      ...NetWorthAccountProductTypeTag
      ...IsDisconnectedLinkedAccount
      ...ViewAccountDialog
      __typename
      accountSleeves {
        id
        ...NetWorthAccountSleeveRow
      }
      custody
      displayBalance {
        amount
      }
      hiddenFromClient
      hiddenFromNetWorth
      id
      lastUpdatedAt
      performance {
        timePeriod
        twr
      }
      productType
      providerAccountId
      source
      type
      mask
    }
    assets {
      ...NetWorthAssetDescriptionRow
      ...NetWorthAssetMenu
      ...ViewAssetDialog
      __typename
      hiddenFromClient
      hiddenFromNetWorth
      id
      lastUpdatedAt
      liabilities {
        currentBalance {
          amount
        }
      }
      value {
        amount
      }
    }
    liabilities {
      ...NetWorthLiabilityDescriptionRow
      ...NetWorthLiabilityMenu
      ...ViewLiabilityDialog
      __typename
      currentBalance {
        amount
      }
      hiddenFromClient
      hiddenFromNetWorth
      id
      lastUpdatedAt
    }
  }
`);
type Res = ResultOf<typeof NetWorthAccountGroupTableFragment>;
type Item = Res['accounts'][number] | Res['assets'][number] | Res['liabilities'][number];

const NET_WORTH_COLUMN_WIDTHS: Record<string, string> = {
  description: 'w-[35%]',
  mask: 'w-[14%]',
  value: 'w-[14%]',
  performance: 'w-[14%]',
  actions: 'w-[23%]',
};

function ItemValueCell({
  item,
  isSmallScreen,
  portfolioItemGroupName,
}: {
  readonly item: Item;
  readonly isSmallScreen: boolean;
  readonly portfolioItemGroupName: Res['name'];
}) {
  const valueSubtitle = item.__typename === 'Asset' ? assetValueSubtitle(item) : null;
  const secondaryValueSubtitle = portfolioItemSecondaryValueSubtitle(item);
  const isDisconnectedLinkedItem = Boolean(
    item.__typename === 'Account' && item.providerAccountId && isDisconnectedLinkedAccount({ accountFrag: item }),
  );

  return (
    <div className="flex flex-col gap-0 items-end">
      <div className={cn('flex', isSmallScreen ? 'flex-col gap-0' : 'flex-row gap-1')}>
        <Text
          color={(() => {
            if (isDisconnectedLinkedItem) return 'secondary';
            if (isSmallScreen) return 'dark';
            return 'default';
          })()}
          variant={isSmallScreen ? 'body-sm' : undefined}
        >
          {item.__typename === 'Account' && accountValueTitle({ account: item, portfolioItemGroupName })}
          {item.__typename === 'Asset' && assetValueTitle(item)}
          {item.__typename === 'Liability' && liabilityValueTitle(item)}
        </Text>
        {isDisconnectedLinkedItem && item.__typename === 'Account' && !isSmallScreen && (
          <Tooltip>
            <TooltipTrigger render={<Info className="text-secondary" />} />
            <TooltipContent>{`Last updated ${DateTime.fromISO(
              item.lastUpdatedAt,
            ).toRelative()}. ${providerSourceDisplayName(
              item.source,
            )} has stopped sending us information about this account. Reconnect to keep it up to date.`}</TooltipContent>
          </Tooltip>
        )}
        {isSmallScreen && (
          <Text color="secondary" variant="caption">
            {DateTime.fromISO(item.lastUpdatedAt).toRelative()}
          </Text>
        )}
      </div>
      {valueSubtitle && (
        <Text
          variant={isSmallScreen ? 'caption' : 'body-sm'}
          color="secondary"
          className={`text-right ${isSmallScreen ? 'max-w-[104px]' : 'max-w-none'}`}
        >
          {valueSubtitle}
        </Text>
      )}
      {secondaryValueSubtitle && !isSmallScreen && (
        <Text variant="body-sm" color="secondary" className="text-right">
          {secondaryValueSubtitle}
        </Text>
      )}
    </div>
  );
}

// Hover used to be React state on the table; CSS reveal keeps it local to the row and
// removes a re-render of every row on every pointer move.
function ItemActionsCell({
  item,
  advisorView,
  hideProductTypeIcon,
  onEdit,
}: {
  readonly item: Item;
  readonly advisorView: boolean | undefined;
  readonly hideProductTypeIcon: boolean;
  readonly onEdit: Deprecated_HandleItemUpdate;
}) {
  const isNavigable =
    item.__typename === 'Account' &&
    (item.custody === 'managed' || item.custody === 'held_away' || item.productType === 'cash_management');

  return (
    <div className="flex flex-row items-center justify-end">
      <div className="hidden flex-row gap-1 items-center group-hover:flex has-[[data-popup-open]]:flex">
        {isNavigable ? (
          <>
            <Text variant="body-sm" color="secondary">
              Details
            </Text>
            <ChevronRight color={textColors.secondary} />
          </>
        ) : (
          <>
            <Text variant="body-sm" color="secondary">
              View
            </Text>
            <Maximize color={textColors.secondary} />
          </>
        )}
        {item.__typename === 'Account' && (
          <NetWorthAccountMenu accountFrag={item} advisorView={advisorView} onEdit={onEdit} />
        )}
        {item.__typename === 'Asset' && (
          <NetWorthAssetMenu assetFrag={item} onEdit={onEdit} advisorView={advisorView} />
        )}
        {item.__typename === 'Liability' && (
          <NetWorthLiabilityMenu liabilityFrag={item} onEdit={onEdit} advisorView={advisorView} />
        )}
      </div>
      <div className="flex flex-row gap-1 items-center group-hover:hidden group-has-[[data-popup-open]]:hidden">
        {item.__typename === 'Account' && (
          <NetWorthAccountProductTypeTag accountFrag={item} iconOnly={!hideProductTypeIcon} />
        )}
        <Text variant="body-sm" color="secondary">
          {DateTime.fromISO(item.lastUpdatedAt).toRelative()}
        </Text>
      </div>
    </div>
  );
}

// DataTable has no detail-panel slot, and sleeves are not homogeneous sub-rows, so the
// row slot emits the item row plus its sleeve rows as siblings — same shape as the
// bulk-onboarding table.
function createItemRowSlot({
  onRowClick,
  quickDateForPerformance,
  showAccountSleeves,
  showPerformanceColumn,
}: {
  readonly onRowClick: (item: Item, event: React.MouseEvent<HTMLTableRowElement>) => void;
  readonly quickDateForPerformance: PerformanceTimePeriodName | undefined;
  readonly showAccountSleeves: boolean;
  readonly showPerformanceColumn: boolean;
}) {
  // `slots.row` takes a component, so it has to keep a stable identity across renders
  // or every row remounts.
  return function ItemRowSlot({
    row,
    index: _index,
    children,
    className,
    ...rest
  }: { readonly row: Row<Item>; readonly index: number } & React.ComponentProps<'tr'>) {
    const item = row.original;
    const isDisconnectedLinkedItem = Boolean(
      item.__typename === 'Account' && item.providerAccountId && isDisconnectedLinkedAccount({ accountFrag: item }),
    );
    const sleeves = showAccountSleeves && item.__typename === 'Account' ? (item.accountSleeves ?? []) : [];

    return (
      <React.Fragment>
        <TableRow
          {...rest}
          className={cn('group cursor-pointer border-b-0', className)}
          onClick={(event) => onRowClick(item, event)}
        >
          {children}
        </TableRow>
        {sleeves.map((sleeve) => (
          <NetWorthAccountSleeveRow
            key={sleeve.id}
            accountSleeveFrag={sleeve}
            isDisconnectedLinkedItem={isDisconnectedLinkedItem}
            quickDateForPerformance={quickDateForPerformance}
            showPerformanceColumn={showPerformanceColumn}
          />
        ))}
      </React.Fragment>
    );
  };
}

const ONE_MINUTE_IN_MS = 6000;

const AGGREGATOR_SOURCES: readonly ProviderAccountSource[] = ['PLAID', 'MONEYKIT'];

export const isAggregatedInvestmentAccount = (account: {
  custody: AccountCustodyName;
  source: ProviderAccountSource | null;
  type: PortfolioItemTypeName;
}) => {
  return (
    account.type === 'investment' &&
    account.custody === 'held_away' &&
    !!account.source &&
    AGGREGATOR_SOURCES.includes(account.source)
  );
};

type Props = {
  readonly showGroupTitle?: boolean;
  readonly portfolioItemGroupFrag: FragmentType<typeof NetWorthAccountGroupTableFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
  readonly hiddenAccountsView?: boolean;
  readonly clientUserId?: ID;
  readonly advisorView?: boolean;
  readonly advisorName?: string;
  readonly quickDateForPerformance?: PerformanceTimePeriodName;
  readonly personalPortfolioId: ID | null;
  readonly showAccountSleeves?: boolean;
} & LDProps;

/**
 *
 * @deprecated - We should make this into 3 components - one for each item type (Accounts, assets, liabilities)
 */
function NetWorthAccountGroupTable({
  portfolioItemGroupFrag,
  onEdit,
  clientUserId,
  hiddenAccountsView = false,
  advisorView,
  advisorName,
  showGroupTitle = true,
  quickDateForPerformance,
  personalPortfolioId,
  showAccountSleeves = false,
}: Props) {
  const portfolioItemGroup = unmaskFragment(NetWorthAccountGroupTableFragment, portfolioItemGroupFrag);
  const isSmallScreen = useIsSmallScreen();
  const router = useRouter();
  const preSelectedAccount = portfolioItemGroup.accounts.find((item) => item.id === router.query['editAccountId']);
  // Standard "forceUpdate" pattern: a no-op state setter that triggers a re-render.
  // The state value is intentionally unused.
  // eslint-disable-next-line react/hook-use-state -- forceUpdate pattern
  const [, updateState] = React.useState({});
  const forceUpdate = React.useCallback(() => {
    updateState({});
  }, []);
  const [addItemType, setAddItemType] = useState<ItemType | undefined>(undefined);

  // Update component to refresh updated_at text every minute
  useInterval(() => {
    forceUpdate();
  }, ONE_MINUTE_IN_MS);

  useEffect(() => {
    if (preSelectedAccount) {
      setSelectedItem(preSelectedAccount);
    }
  }, [preSelectedAccount]);

  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const handleDialogClose = () => {
    setSelectedItem(null);
    // Prevent portfolio page from refreshing
    if (!advisorView) {
      history.replaceState({}, '', `/portfolio`);
    }
  };
  const handleEdit = (updateObject: Deprecated_ItemsUpdateObject) => {
    if (!updateObject.persistDialog) {
      handleDialogClose();
    }

    return onEdit(updateObject);
  };
  const hideProductTypeIcon = useMediaQuery('(max-width:1300px)');
  const items: Item[] = [
    ...portfolioItemGroup.accounts,
    ...portfolioItemGroup.assets,
    ...portfolioItemGroup.liabilities,
  ];

  const visibleItems = items.filter(
    (item) => (!hiddenAccountsView && !item.hiddenFromClient) || (hiddenAccountsView && item.hiddenFromClient),
  );

  // Whether the performance column exists is a table-level decision. The MUI version
  // decided it per row, so rows without performance data rendered one cell short and
  // their columns stopped lining up with their neighbours.
  const showPerformanceColumn = Boolean(advisorView && quickDateForPerformance);

  const columns: ColumnDef<Item>[] = [
    {
      id: 'description',
      cell: ({ row }) => {
        const item = row.original;
        const secondaryValueSubtitle = portfolioItemSecondaryValueSubtitle(item);
        return (
          <>
            <div className="pl-2">
              {item.__typename === 'Account' && <NetWorthAccountDescriptionRow accountFrag={item} />}
              {item.__typename === 'Asset' && <NetWorthAssetDescriptionRow assetFrag={item} />}
              {item.__typename === 'Liability' && <NetWorthLiabilityDescriptionRow liabilityFrag={item} />}
            </div>
            {secondaryValueSubtitle && isSmallScreen && (
              <Text variant="caption" color="secondary" className="pl-12">
                {secondaryValueSubtitle}
              </Text>
            )}
          </>
        );
      },
    },
    {
      id: 'mask',
      cell: ({ row }) => (
        <Text>
          {row.original.__typename === 'Account' && row.original.mask
            ? padBulletsToMaskedAccountNumber(row.original.mask)
            : '—'}
        </Text>
      ),
    },
    {
      id: 'value',
      cell: ({ row }) => (
        <ItemValueCell
          item={row.original}
          isSmallScreen={isSmallScreen}
          portfolioItemGroupName={portfolioItemGroup.name}
        />
      ),
    },
    ...(showPerformanceColumn
      ? [
          {
            id: 'performance',
            cell: ({ row }) => {
              const item = row.original;
              if (item.__typename !== 'Account' || !item.performance || !quickDateForPerformance) return null;
              return (
                <div className="text-right">
                  <ColorCodedPerformanceTypography
                    performance={parseFloat(
                      performanceFromQuickDate({
                        performanceOverTime: item.performance,
                        quickDate: quickDateForPerformance,
                      })?.twr ?? '',
                    )}
                  />
                </div>
              );
            },
          } satisfies ColumnDef<Item>,
        ]
      : []),
    ...(isSmallScreen
      ? []
      : [
          {
            id: 'actions',
            cell: ({ row }) => (
              <ItemActionsCell
                item={row.original}
                advisorView={advisorView}
                hideProductTypeIcon={hideProductTypeIcon}
                onEdit={onEdit}
              />
            ),
          } satisfies ColumnDef<Item>,
        ]),
  ];

  const handleRowClick = useCallback(
    (item: Item, event: React.MouseEvent<HTMLTableRowElement>) => {
      // Let the re-connect button handle its own click.
      if (event.target instanceof HTMLElement && event.target.nodeName === 'BUTTON') return;

      const salesforceAccountId = advisorView ? router.query['salesforceAccountId'] : null;
      assert(
        isNil(salesforceAccountId) || typeof salesforceAccountId === 'string',
        'salesforceAccountId must be a string or undefined',
      );

      if (item.__typename === 'Account' && (item.custody === 'managed' || item.productType === 'cash_management')) {
        void router.push(getManagedAccountURI(item.id, advisorView, salesforceAccountId));
        return;
      }
      if (item.__typename === 'Account' && isAggregatedInvestmentAccount(item)) {
        void router.push(getHeldawayAccountURI(item, advisorView, salesforceAccountId));
        return;
      }
      setSelectedItem(item);
    },
    [advisorView, router],
  );

  const RowSlot = useMemo(
    () =>
      createItemRowSlot({
        onRowClick: handleRowClick,
        quickDateForPerformance,
        showAccountSleeves,
        showPerformanceColumn,
      }),
    [handleRowClick, quickDateForPerformance, showAccountSleeves, showPerformanceColumn],
  );

  return (
    <div className={cn('opacity-100', isSmallScreen ? 'pt-4' : 'pt-0')}>
      {showGroupTitle && (
        <NetWorthAccountGroupTableTitle
          hiddenAccountsView={hiddenAccountsView}
          hasItems={items.length === 0}
          portfolioItemGroupFrag={portfolioItemGroup}
          advisorName={advisorName}
          advisorView={advisorView}
          clientUserId={clientUserId}
          personalPortfolioId={personalPortfolioId}
          onClickAddAccountButton={(itemType) => {
            setAddItemType(itemType);
          }}
        />
      )}
      {/* An empty group is just its heading — the MUI table rendered nothing below it.
          DataTable's empty state can't be turned off through `emptyState`: it falls back
          to "No results." on null and still lays out a full-height row for an empty node,
          so the table has to be left out entirely. */}
      {visibleItems.length > 0 && (
        <DataTable<Item>
          name="net-worth-account-group"
          columns={columns}
          data={visibleItems}
          features={{ header: false }}
          slots={{ row: RowSlot }}
          slotProps={{ table: { 'aria-label': 'Asset Group Table' } }}
          classNames={{
            // The page around this supplies the chrome; every cell was a NoBorderTableCell.
            root: 'rounded-none border-0',
            tableContent: 'border-0 mx-0',
            cell: ({ cell }) => NET_WORTH_COLUMN_WIDTHS[cell.column.id] ?? '',
          }}
        />
      )}
      {selectedItem && selectedItem.__typename === 'Account' && (
        <ViewAccountDialog onEdit={handleEdit} accountFrag={selectedItem} onClose={handleDialogClose} />
      )}
      {selectedItem && selectedItem.__typename === 'Asset' && (
        <ViewAssetDialog
          onEdit={handleEdit}
          assetFrag={selectedItem}
          onClose={handleDialogClose}
          clientUserId={clientUserId}
        />
      )}
      {selectedItem && selectedItem.__typename === 'Liability' && (
        <ViewLiabilityDialog onEdit={handleEdit} liabilityFrag={selectedItem} onClose={handleDialogClose} />
      )}
      {addItemType && (
        <AddItemDialog
          hiddenFromClient={hiddenAccountsView}
          onSuccess={(...args) => {
            setAddItemType(undefined);
            return onEdit(...args);
          }}
          onClose={() => {
            setAddItemType(undefined);
          }}
          initialItemType={addItemType}
        />
      )}
    </div>
  );
}

export default withLDConsumer()(NetWorthAccountGroupTable);
